<template>
  <ResponsiveDialog
    v-model="visible"
    title="일정 정보"
    width="520px"
    @close="handleClose"
  >
    <!--
      헤더는 "이게 무엇인가" 만 말한다 — 종류 칩과 반복 여부.
      제목은 본문 히어로가 받는다(20px 이라 헤더 줄에 넣으면 닫기 버튼과 부딪힌다).
      닫기 버튼은 ResponsiveDialog(모바일)와 el-dialog(데스크톱)가 각자 붙여 주므로
      여기서 또 만들지 않는다. 예전에 시트마다 닫기가 두 개씩 생겼던 자리다.
    -->
    <template #header>
      <div v-if="event" class="ei__head">
        <span class="tone-chip ei__type" :class="`ei__type--${eventTypeKey}`">
          {{ getEventTypeLabel(eventTypeKey) }}
        </span>
        <!-- 반복 소속 여부. 수정/삭제 때 '이 일정만 / 전체' 를 묻게 되는 근거라
             상세를 열자마자 보여야 한다. seriesId 가 null 이면 단발이거나
             이미 시리즈에서 분리된 예외다. -->
        <span v-if="event.seriesId" class="tone-chip ei__rep">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M17 2l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <path d="M7 22l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          반복
        </span>
      </div>
    </template>

    <div v-if="event" class="ei" :class="`evt-${eventTypeKey}`">
      <!-- 히어로: 상단 4px 종류색 바 + 제목. -->
      <div class="ei__hero">
        <span class="ei__bar" />
        <h2 class="ei__title">{{ event.title }}</h2>
      </div>

      <!-- 정보 행: [16px 아이콘][값 굵게 / 캡션]. 아이콘 열 폭을 고정해야
           일시·장소·담당자의 값이 같은 x 에서 시작한다. -->
      <div class="ei__rows">
        <div class="ei__row">
          <span class="ei__ico" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" />
            </svg>
          </span>
          <span class="ei__rbody">
            <span class="ei__val u-num">{{ formatEventDateTime(event) }}</span>
            <span class="ei__cap">일시</span>
          </span>
        </div>

        <div v-if="getEventLocation(event)" class="ei__row">
          <span class="ei__ico" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" />
            </svg>
          </span>
          <span class="ei__rbody">
            <span class="ei__val">{{ getEventLocation(event) }}</span>
            <!-- 서버 AgendaItem 의 location 은 { id, name } 뿐이라 주소가 없다.
                 대신 등록 장소인지 직접입력인지를 캡션으로 말해 준다 —
                 id 가 null 이면 locationCustom 을 그대로 담아 내려준 값이다. -->
            <span class="ei__cap">{{ event.location && event.location.id ? '등록 장소' : '직접 입력' }}</span>
          </span>
        </div>

        <div v-if="event.owner" class="ei__row">
          <el-avatar
            :size="20"
            :src="getProfileImageUrl(event.owner)"
            class="ei__avatar"
          >{{ getProfileInitial(event.owner) }}</el-avatar>
          <span class="ei__rbody">
            <span class="ei__val">{{ event.owner.nickname }}</span>
            <span class="ei__cap">{{ event.department ? event.department.name : '담당자' }}</span>
          </span>
        </div>

        <div v-if="event.description" class="ei__row ei__row--desc">
          <span class="ei__ico" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 3.5h9.5L19 8v12.5H5z" /><path d="M14 3.5V8h5M8 13h8M8 16.5h5" />
            </svg>
          </span>
          <div class="ei__rbody">
            <span class="ei__cap ei__cap--top">설명</span>
            <p class="ei__desc">{{ event.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <!--
        푸터 버튼을 래퍼 <div> 로 감싸지 않는다. ResponsiveDialog 의
        `.rd-footer > .el-button { flex: 1 }` 이 **직계 자식**에만 걸려 있어서,
        감싸는 순간 모바일 시트에서 버튼이 풀폭으로 펴지지 않는다.
      -->
      <el-button class="ei__btn" @click="handleClose">닫기</el-button>
      <el-button v-if="canEditEvent" class="ei__btn" type="primary" @click="handleManage">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 20h4l10-10-4-4L4 16z" /><path d="M13.5 6.5l4 4" />
        </svg>
        수정
      </el-button>
    </template>
  </ResponsiveDialog>

  <!-- 수정 모달 -->
  <EventDialog
    :visible="editDialogVisible"
    :event="editEventData"
    @update:visible="editDialogVisible = $event"
    @event-saved="handleEventSaved"
    @event-deleted="handleEventDeleted"
  />
</template>

<script>
import { ref, computed } from 'vue'
import { getProfileImageUrl, getProfileInitial } from '../utils/profileUtils'
import EventDialog from './EventDialog.vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import { getEventTypeLabel } from '@/utils/eventTypes'

export default {
  name: 'EventInfoDialog',
  components: {
    EventDialog,
    ResponsiveDialog
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'close', 'refresh'],
  setup(props, { emit }) {
    const editDialogVisible = ref(false)
    const editEventData = ref(null)

    const visible = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      }
    })

    const eventTypeKey = computed(() => props.event?.eventType || 'work')

    /**
     * 편집 가능 여부는 **서버가 내려준 canEdit** 를 그대로 쓴다.
     *
     * 예전에는 extendedProps.user.id 를 자기 id 와 비교했는데, 캘린더가
     * FullCalendar 형태를 버리고 AgendaItem 을 넘기게 된 뒤로 그 경로는
     * 항상 undefined 였다. 결과적으로 grade>=3 이 아닌 사람에게는
     * '관리' 버튼이 아예 보이지 않았다 — 12명 중 8명이 자기 일정을
     * 캘린더에서 편집할 수 없었다는 뜻이다.
     * 권한 규칙을 클라이언트에서 다시 구현하지 않는 편이 애초에 옳다.
     */
    const canEditEvent = computed(() => !!props.event?.canEdit)

    function formatEventDateTime(event) {
      if (!event) return ''
      const start = new Date(event.start)
      const end = new Date(event.end)

      if (event.allDay) {
        const startDateStr = start.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'short'
        })
        const endDateStr = end.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'short'
        })
        if (start.toDateString() === end.toDateString()) {
          return `${startDateStr} · 종일`
        }
        return `${startDateStr} ~ ${endDateStr} · 종일`
      }

      const dateFmt = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }
      const startStr = start.toLocaleString('ko-KR', dateFmt)
      const endStr = end.toLocaleString('ko-KR', dateFmt)

      if (start.toDateString() === end.toDateString()) {
        const endTime = end.toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit'
        })
        return `${startStr} ~ ${endTime}`
      }
      return `${startStr} ~ ${endStr}`
    }

    function getEventLocation(event) {
      // AgendaItem 은 등록 장소와 직접입력을 location 하나로 합쳐 내려준다
      // (직접입력이면 { id: null, name }). locationCustom 은 하위호환용 폴백이다.
      return event?.location?.name || event?.locationCustom || ''
    }

    function handleClose() {
      // 스스로 닫아야 한다. 예전에는 emit('close') 만 했는데 부모(Calendar)가
      // @close 를 듣지 않아서 [닫기] 버튼이 아무 일도 하지 않았다.
      // 바깥 클릭이나 X 는 ResponsiveDialog 가 v-model 을 내려서 닫혔기 때문에
      // 유독 [닫기] 버튼만 먹통이었다.
      visible.value = false
      emit('close')
    }

    /**
     * AgendaItem → EventDialog 가 기대하는 평평한 모양.
     *
     * 여기가 두 형태의 유일한 경계다. 예전에는 extendedProps 에서 읽었는데
     * 그 경로가 전부 undefined 라 startDate 가 `new Date(undefined)` = Invalid Date
     * 가 되고, 폼에 'NaN-NaN-NaN' 이 채워진 채 열렸다. 그 문자열은 truthy 라
     * 필수값 검증까지 통과해서 그대로 저장 요청이 나갔다.
     *
     * seriesId 를 함께 넘기는 것이 반복 일정의 '이 일정만 / 이후 전부 / 전체'
     * 를 물어볼 수 있게 하는 조건이다.
     */
    function handleManage() {
      if (!props.event) return
      const e = props.event
      editEventData.value = {
        id: e.id,
        title: e.title,
        description: e.description,
        startDate: e.start,
        endDate: e.end,
        locationId: e.location?.id ?? null,
        locationCustom: e.locationCustom || '',
        eventType: e.eventType,
        isAllDay: e.allDay,
        userId: e.owner?.id,
        // 담당 행에 이름을 찍고, 수정 가능 여부를 서버 판단 그대로 쓰게 함께 넘긴다
        owner: e.owner || null,
        canEdit: e.canEdit,
        seriesId: e.seriesId || null
      }
      editDialogVisible.value = true
    }

    function handleEventSaved() {
      editDialogVisible.value = false
      visible.value = false
      emit('refresh')
    }

    function handleEventDeleted() {
      editDialogVisible.value = false
      visible.value = false
      emit('refresh')
    }

    return {
      visible,
      canEditEvent,
      editDialogVisible,
      editEventData,
      eventTypeKey,
      formatEventDateTime,
      getEventLocation,
      getEventTypeLabel,
      getProfileImageUrl,
      getProfileInitial,
      handleClose,
      handleManage,
      handleEventSaved,
      handleEventDeleted
    }
  }
}
</script>

<style scoped>
/* ---------- 헤더 ---------- */
.ei__head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
  /* 데스크톱 el-dialog 의 닫기 버튼은 헤더 오른쪽에 절대 배치된다.
     칩이 그 아래로 흘러 들어가지 않게 오른쪽을 비워 둔다. */
  padding-right: var(--sp-6);
}

/*
 * 종류 칩. .tone-chip 위에 유형색만 덧입힌다.
 * 옅은 배경 + 진한 글자 쌍은 tokens.css 가 정의한 것만 쓴다 — 원색 배경에
 * 흰 글자를 얹으면 휴무(회색)·점검(주황)에서 대비가 무너진다.
 */
.ei__type--work { background: var(--color-primary-soft); color: var(--color-primary-strong); }
.ei__type--substitute { background: var(--color-success-soft); color: var(--color-success-strong); }
.ei__type--fieldwork { background: var(--evt-fieldwork-bg); color: var(--evt-fieldwork-fg); }
.ei__type--inspection { background: var(--evt-inspection-bg); color: var(--evt-inspection-fg); }
.ei__type--dayoff { background: var(--color-surface-2); color: var(--color-text-muted); }
.ei__type--vacation { background: var(--color-danger-soft); color: var(--color-danger-strong); }

.ei__rep { color: var(--color-text-muted); }

/* ---------- 본문 ---------- */
.ei {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

/*
 * 히어로.
 *
 * 예전에는 유형색으로 135deg 그라데이션을 깐 컬러 배너였다. 두 가지가 문제였다.
 *   1) 두 번째 색 스톱이 --evt-* 토큰과 무관한 별도 hex 6개(#3b82f6, #22c55e …)라
 *      같은 '업무' 인데 캘린더 칩과 이 배너의 색이 서로 달랐다.
 *   2) 배너 위 흰 글자가 색에 따라 대비가 들쭉날쭉했다 — 특히 휴무(회색)에서.
 *
 * 지금은 TaskCard 와 같은 형태다: 카드 위쪽을 가로지르는 3~4px 색 띠 + 흰 배경.
 * 종류는 헤더 칩과 이 띠가 말하고, 제목은 항상 --color-text 위에서 읽힌다.
 */
.ei__hero {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  overflow: hidden;
}

.ei__bar { display: block; height: 4px; background: var(--evt-work); }
.evt-work .ei__bar { background: var(--evt-work); }
.evt-substitute .ei__bar { background: var(--evt-substitute); }
.evt-fieldwork .ei__bar { background: var(--evt-fieldwork); }
.evt-inspection .ei__bar { background: var(--evt-inspection); }
.evt-dayoff .ei__bar { background: var(--evt-dayoff); }
.evt-vacation .ei__bar { background: var(--evt-vacation); }

.ei__title {
  margin: 0;
  padding: var(--sp-3) var(--sp-4) var(--sp-4);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.01em;
  color: var(--color-text);
  line-height: 1.35;
  word-break: break-word;
}

/* ---------- 정보 행 ---------- */
.ei__rows {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: 0 var(--sp-1);
}

.ei__row {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  min-width: 0;
}

/*
 * 아이콘 열.
 * 예전에는 44×44 회색 사각형이었다. 행이 셋뿐인 다이얼로그에서 그 덩어리 셋이
 * 제목 다음으로 눈에 띄었고, 정작 값(일시·장소)은 그 옆에서 밀려 있었다.
 * 20px 열에 16px 라인 아이콘만 둔다 — 담당자 아바타도 같은 폭이라 값의
 * 시작 x 가 네 행 모두 같다.
 */
.ei__ico {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
  margin-top: 1px;
}

.ei__avatar {
  flex-shrink: 0;
  margin-top: 1px;
  border: 1px solid var(--color-border);
  box-shadow: none;
  font-size: var(--fs-2xs);
}

.ei__rbody {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ei__val {
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: 1.45;
  word-break: break-word;
}

.ei__cap {
  font-size: var(--fs-xs);
  color: var(--color-text-subtle);
}
.ei__cap--top { margin-bottom: var(--sp-1); }

/* 설명은 한 단계 눌린 면에 둔다. 본문 글자 크기를 유지해야 긴 메모가 읽힌다. */
.ei__desc {
  margin: 0;
  padding: var(--sp-3);
  background: var(--color-surface-3);
  border: 1px solid var(--color-border);
  border-radius: var(--r-md);
  font-size: var(--fs-md);
  color: var(--color-text);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ---------- 푸터 ---------- */
.ei__btn { min-height: 34px; }
.ei__btn svg { margin-right: 4px; }

/* ---------- 모바일 ---------- */
@media (max-width: 767px) { /* --bp-mobile-max */
  .ei { gap: var(--sp-3); }
  .ei__title { font-size: var(--fs-lead); padding: var(--sp-3) var(--sp-3) var(--sp-4); }
  /* 바텀시트 푸터는 한 손 엄지로 누르는 자리다. 44 로는 얇다. */
  .ei__btn { min-height: var(--tap-lg); font-weight: var(--fw-semibold); }
  /* 헤더 닫기 버튼이 시트에서는 흐름 안(margin-left:auto)에 있어 여백이 필요 없다 */
  .ei__head { padding-right: 0; }
}
</style>
